/**
 * riskSignals — pure, read-only helper that summarises lightweight risk
 * indicators for a job from the existing scheduling event log + audit trail.
 *
 * Source data:
 *   • SchedulingEvent.payload.attribution.jobId  (reschedules / extensions)
 *   • SchedulingAuditEntry.metadata.attribution.jobId  (conflicts)
 *
 * No new state, no new models, no engine changes. Session-local only —
 * mirrors the in-memory nature of eventLog/auditTrail.
 *
 * Thresholds (beta-tuned, conservative):
 *   • reschedules >= 2  → "Rescheduled N×"
 *   • extensions  >= 1  → "Previously extended"
 *   • conflicts   >= 1  → "Conflict-prone"
 */
import type { SchedulingEvent, SchedulingAuditEntry, SchedulingAttribution } from "./eventBus";

export interface JobRiskSignals {
  reschedules: number;
  extensions: number;
  conflicts: number;
  /** True if at least one badge would render. Convenience for callers. */
  hasAny: boolean;
}

const EMPTY: JobRiskSignals = { reschedules: 0, extensions: 0, conflicts: 0, hasAny: false };

function attributionJobId(payload: unknown): string | undefined {
  if (!payload || typeof payload !== "object") return undefined;
  const attr = (payload as Record<string, unknown>).attribution as
    | SchedulingAttribution
    | undefined;
  return attr?.jobId;
}

/** Summarise risk signals for a single job. */
export function getJobRiskSignals(
  jobId: string,
  eventLog: SchedulingEvent[],
  auditTrail: SchedulingAuditEntry[],
): JobRiskSignals {
  if (!jobId) return EMPTY;

  let reschedules = 0;
  let extensions = 0;
  for (const evt of eventLog) {
    if (evt.type !== "EVENT_RESCHEDULED") continue;
    if (attributionJobId(evt.payload) !== jobId && evt.jobId !== jobId) continue;
    const subtype = (evt.payload as Record<string, unknown>)?.subtype;
    if (subtype === "extension") extensions += 1;
    else reschedules += 1;
  }

  let conflicts = 0;
  for (const entry of auditTrail) {
    if (entry.type !== "CONFLICT_DETECTED") continue;
    if (attributionJobId(entry.metadata) !== jobId && entry.jobId !== jobId) continue;
    conflicts += 1;
  }

  const hasAny = reschedules >= 2 || extensions >= 1 || conflicts >= 1;
  return { reschedules, extensions, conflicts, hasAny };
}

/**
 * Count how many currently-scheduled jobs assigned to a given crew/assignee
 * carry at least one risk signal. Used by the SchedulingConfirmInspector to
 * surface a "high-risk load" hint when the chosen crew already shoulders
 * multiple risky jobs. Threshold (>=2) is applied at the call site.
 *
 * `assigneeName` matches against either the internal crew name or the
 * subcontractor name on the job (both stored as plain strings).
 */
export function countCrewRiskyJobs(
  assigneeName: string | undefined | null,
  scheduledJobs: Array<{ id: string; assignedCrew?: string; assignedSubcontractor?: string }>,
  eventLog: SchedulingEvent[],
  auditTrail: SchedulingAuditEntry[],
): number {
  if (!assigneeName) return 0;
  let count = 0;
  for (const job of scheduledJobs) {
    if (job.assignedCrew !== assigneeName && job.assignedSubcontractor !== assigneeName) continue;
    if (getJobRiskSignals(job.id, eventLog, auditTrail).hasAny) count += 1;
  }
  return count;
}
